<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import TranslationSettingsPopover from './TranslationSettingsPopover.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const popoverVisible = ref(false);
let hoverTimer = null;

const prefs = computed(() => store.getters.prefsFor(props.conversationId));

const isTranslating = computed(
  () => store.getters.isTranslatingConversation(props.conversationId)
);

const ensureLoaded = () => {
  store.dispatch('loadConversationPrefs', props.conversationId);
};

onMounted(ensureLoaded);
watch(() => props.conversationId, ensureLoaded);

const toggleInbound = async () => {
  if (isTranslating.value) return;
  const next = !prefs.value.inboundOn;
  await store.dispatch('updateConversationPrefs', {
    conversationId: props.conversationId,
    patch: { inboundOn: next },
  });
  if (next) {
    store.dispatch('batchTranslateConversation', {
      conversationId: props.conversationId,
      direction: 'in',
    });
  }
};

const handleEnter = () => {
  clearTimeout(hoverTimer);
  popoverVisible.value = true;
};

const handleLeave = () => {
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => {
    popoverVisible.value = false;
  }, 200);
};

defineExpose({});
</script>

<template>
  <div
    class="relative inline-flex items-center"
    @mouseenter="handleEnter"
    @mouseleave="handleLeave"
  >
    <button
      type="button"
      class="px-2 py-1 text-xs font-medium rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      :class="
        prefs.inboundOn
          ? 'bg-n-brand text-white border-transparent'
          : 'bg-n-alpha-1 text-n-slate-11 border-n-weak hover:bg-n-alpha-2'
      "
      :title="
        isTranslating
          ? t('CUSTOM.TRANSLATION.INBOUND_BUSY')
          : prefs.inboundOn
            ? t('CUSTOM.TRANSLATION.INBOUND_DISABLE_TITLE')
            : t('CUSTOM.TRANSLATION.INBOUND_ENABLE_TITLE')
      "
      :disabled="isTranslating"
      @click="toggleInbound"
    >
      <span class="inline-flex items-center gap-1">
        <span class="text-[10px] uppercase tracking-wide">AI</span>
        <span v-if="isTranslating" class="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
        <span>{{
          isTranslating
            ? t('CUSTOM.TRANSLATION.TRANSLATING')
            : prefs.inboundOn
              ? t('CUSTOM.TRANSLATION.TRANSLATE_ON')
              : t('CUSTOM.TRANSLATION.TRANSLATE')
        }}</span>
      </span>
    </button>
    <TranslationSettingsPopover
      v-if="popoverVisible"
      :conversation-id="conversationId"
      class="absolute right-0 top-full mt-2 z-40"
      @stay="handleEnter"
      @leave="handleLeave"
    />
  </div>
</template>
